import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../api';

// 회원가입 시에는 "사내임직원/협력사" 구분(memberType)만 선택하고, 실제 메뉴별 접근권한·부서는
// 가입 후 최고관리자가 계정관리 화면(권한설정)에서 배정합니다. (직급은 계정관리에서 등록해둔
// 목록 중 바로 선택) 관리자/생산관리자 같은 "역할" 개념은 더 이상 없으며, 모든 일반 계정은
// role='member'로 동일하게 등록되고 회원구분(member_type)만 staff/partner로 나뉩니다.
const ACCOUNT_TYPE_OPTIONS = [
  { value: 'staff',   label: '사내임직원' },
  { value: 'partner', label: '협력사' },
  { value: 'display', label: '생산현황판 전용' },
];

export default function Login() {
  const [tab, setTab] = useState('login');
  const [form, setForm] = useState({
    username: '', password: '', confirmPassword: '',
    name: '', memberType: 'staff', position: '',
    company: '', department: '', address: '', phone: '', email: '', emergency_phone: '',
  });
  const [message, setMessage] = useState('');
  const [msgType, setMsgType] = useState('warn'); // warn / success / error
  const [loading, setLoading] = useState(false);
  // 사외 중복 로그인 메일 인증 단계 { id, maskedEmail } — null이면 일반 로그인 화면
  const [verify, setVerify] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [positionOptions, setPositionOptions] = useState([]);
  // 환경설정(2026-09) — 로그인화면 배지/문구의 회사 브랜드명/이니셜. 로그인 전이라
  // 인증 없이 접근 가능한 /company-info/brand-public을 사용한다.
  const [brand, setBrand] = useState({ brand_name: 'NINE', brand_initial: 'N', brand_suffix: 'IMS' });
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 이미 로그인된 다른 탭이 있어서(BroadcastChannel로) 이 탭도 방금 세션을 넘겨받은 경우 —
  // 로그인 화면에 계속 머무르지 않고 바로 원래 가려던 곳(또는 홈)으로 이동시킨다.
  useEffect(() => {
    if (!user) return;
    const from = location.state?.from;
    // 생산현황 전용 계정은 직원현황 등 다른 메뉴 권한이 없을 수 있으므로, 기본 목적지인
    // 생산현황(/display)으로 바로 보낸다.
    const dest = from ? `${from.pathname}${from.search || ''}` : (user.accountType === 'display_only' ? '/display' : '/');
    navigate(dest, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // 다른 위치에서 인증 후 접속하여 로그아웃된 경우 안내 문구 표시
  useEffect(() => {
    const n = sessionStorage.getItem('ims_logout_notice');
    if (n) { sessionStorage.removeItem('ims_logout_notice'); setMessage(n); setMsgType('warn'); }
  }, []);

  // 계정관리(조직구성)에 등록된 직급 목록을 회원가입 화면에서 그대로 선택할 수 있도록 불러옴 (로그인 불필요)
  useEffect(() => {
    API.get('/org/public')
      .then(r => setPositionOptions(r.data.positions || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    API.get('/company-info/brand-public')
      .then(r => setBrand({
        brand_name: r.data.brand_name || 'NINE',
        brand_initial: r.data.brand_initial || 'N',
        brand_suffix: r.data.brand_suffix || 'IMS',
      }))
      .catch(() => {});
  }, []);

  const handleChange = e => {
    const { name, type, value, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const setMsg = (text, type = 'warn') => { setMessage(text); setMsgType(type); };

  // 로그인
  const handleLogin = async () => {
    if (!form.username || !form.password) { setMsg('아이디와 비밀번호를 입력해주세요.', 'error'); return; }
    setLoading(true);
    try {
      const res = await API.post('/auth/login', { username: form.username, password: form.password });
      if (res.data.requireVerification) {
        setVerify({ id: res.data.verificationId, maskedEmail: res.data.maskedEmail });
        setVerifyCode('');
        setMsg(res.data.message, 'warn');
        return;
      }
      login(res.data.user, res.data.token, res.data.loginLogId);
      // 회원가입 후 첫 로그인이면 홈 화면에서 "상세정보를 입력해주세요" 안내 배너를 띄우기 위한
      // 표시만 세션에 남겨둔다(로그아웃/브라우저 종료 시 자연히 사라짐 — sessionStorage 사용).
      if (res.data.isFirstLogin) sessionStorage.setItem('ims_first_login_pending', '1');
      // 권한 확인 등으로 로그인 화면으로 왔던 경우(예: 채팅 팝업창), 로그인 후 홈이 아니라
      // 원래 가려던 경로로 돌아간다. state가 없으면(직접 /login으로 들어온 경우) 기존처럼 홈으로.
      const from = location.state?.from;
      const dest = from ? `${from.pathname}${from.search || ''}` : (res.data.user?.accountType === 'display_only' ? '/display' : '/');
      navigate(dest, { replace: true });
    } catch (err) {
      setMsg(err.response?.data?.message || '오류가 발생했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 사외 중복 로그인 — 인증번호 확인(성공하면 사내 접속이 로그아웃되고, 아이디/비밀번호를 다시 입력해 로그인)
  const handleVerify = async () => {
    if (!/^\d{6}$/.test(verifyCode.trim())) { setMsg('6자리 인증번호를 입력해주세요.', 'error'); return; }
    setLoading(true);
    try {
      const res = await API.post('/auth/duplicate-login/verify', { verificationId: verify.id, code: verifyCode.trim() });
      setVerify(null); setVerifyCode('');
      setForm(f => ({ ...f, password: '' }));
      setMsg(res.data.message, 'success');
    } catch (err) {
      setMsg(err.response?.data?.message || '오류가 발생했습니다.', 'error');
    } finally { setLoading(false); }
  };
  const handleResend = async () => {
    try {
      const res = await API.post('/auth/duplicate-login/resend', { verificationId: verify.id });
      setMsg(res.data.message, 'success');
    } catch (err) { setMsg(err.response?.data?.message || '재발송에 실패했습니다.', 'error'); }
  };
  const cancelVerify = () => { setVerify(null); setVerifyCode(''); setForm(f => ({ ...f, password: '' })); setMessage(''); };

  // 회원가입
  const handleRegister = async () => {
    if (!form.username || !form.password || !form.name) {
      setMsg('아이디, 비밀번호, 이름은 필수입니다.', 'error'); return;
    }
    if (form.password !== form.confirmPassword) {
      setMsg('비밀번호가 일치하지 않습니다.', 'error'); return;
    }
    if (form.password.length < 6) {
      setMsg('비밀번호는 6자 이상이어야 합니다.', 'error'); return;
    }
    setLoading(true);
    try {
      const res = await API.post('/auth/register', {
        username: form.username,
        password: form.password,
        name: form.name,
        // 생산현황 전용 계정은 실제 직원이 아니므로 구분/직급/회사/연락처 등은 보내지 않는다.
        accountType: form.memberType === 'display' ? 'display' : undefined,
        ...(form.memberType === 'display' ? {} : {
          memberType: form.memberType,
          position: form.position,
          // 부서는 가입 시 입력하지 않음(관리자가 배정) — 다만 협력사는 담당부서만 예외적으로 받아서
          // department 기본값으로 저장해둠(계정관리에서 그대로 보이거나 관리자가 다시 바꿀 수 있음)
          department: form.memberType === 'partner' ? form.department : '',
          company: form.company,
          address: form.address,
          phone: form.phone,
          email: form.email,
          emergency_phone: form.emergency_phone,
        }),
      });
      setMsg(res.data.message, 'success');
      setTab('login');
    } catch (err) {
      setMsg(err.response?.data?.message || '오류가 발생했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const inp = {
    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
    borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
    marginBottom: 10, outline: 'none'
  };
  const lbl = { fontSize: 12, color: '#888', display: 'block', marginBottom: 3, fontWeight: 500 };
  const required = { color: '#dc2626', marginLeft: 2 };
  const divider = { fontSize: 11, fontWeight: 600, color: '#aaa', letterSpacing: '0.05em', textTransform: 'uppercase', margin: '14px 0 8px', paddingBottom: 4, borderBottom: '1px solid #f0f0f0' };

  const msgBg    = { warn:'#fff3cd', success:'#d1fae5', error:'#fee2e2' };
  const msgColor = { warn:'#856404', success:'#065f46', error:'#dc2626' };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', width: 420, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>

        {/* 로고 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div style={{ width: 47, height: 44, background: '#185FA5', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 26 }}>{brand.brand_initial}</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 17, color: '#1a1a1a' }}>{brand.brand_name} {brand.brand_suffix}</div>
            <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>Integrated Management System</div>
          </div>
        </div>

        {/* 탭 */}
        <div style={{ display: 'flex', marginBottom: 20, borderBottom: '1.5px solid #eee' }}>
          {['login', 'register'].map(t => (
            <button key={t}
              onClick={() => { setTab(t); setMessage(''); }}
              style={{ flex: 1, padding: '10px 0', background: 'none', border: 'none',
                borderBottom: tab === t ? '2px solid #185FA5' : 'none',
                cursor: 'pointer', fontSize: 14, fontWeight: tab === t ? 600 : 400,
                color: tab === t ? '#185FA5' : '#888', marginBottom: -1.5 }}>
              {t === 'login' ? '로그인' : '회원가입'}
            </button>
          ))}
        </div>

        {/* ── 로그인 폼 ── */}
        {tab === 'login' && verify && (
          <div>
            <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6, marginBottom: 10 }}>
              사내에서 이미 접속 중인 계정입니다.<br />
              <strong>{verify.maskedEmail}</strong>로 보낸 6자리 인증번호를 입력하면 사내 접속이 로그아웃됩니다.
              (유효시간 5분)
            </div>
            <input value={verifyCode} onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="인증번호 6자리" inputMode="numeric" autoFocus style={{ ...inp, letterSpacing: 6, textAlign: 'center', fontSize: 18 }}
              onKeyDown={e => e.key === 'Enter' && handleVerify()} />
            <button onClick={handleVerify} disabled={loading}
              style={{ width: '100%', padding: '12px', background: loading ? '#aaa' : '#185FA5', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', marginTop: 4 }}>
              {loading ? '확인 중...' : '인증 확인'}
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
              <button onClick={handleResend} style={{ background: 'none', border: 'none', color: '#185FA5', cursor: 'pointer', fontSize: 13 }}>인증번호 다시 받기</button>
              <button onClick={cancelVerify} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 13 }}>취소</button>
            </div>
          </div>
        )}
        {tab === 'login' && !verify && (
          <div>
            <label style={lbl}>아이디</label>
            <input name="username" placeholder="아이디 입력" value={form.username} onChange={handleChange} style={inp}
              onKeyDown={e => e.key === 'Enter' && handleLogin()} />
            <label style={lbl}>비밀번호</label>
            <input name="password" type="password" placeholder="비밀번호 입력" value={form.password} onChange={handleChange} style={inp}
              onKeyDown={e => e.key === 'Enter' && handleLogin()} />
            <button onClick={handleLogin} disabled={loading}
              style={{ width: '100%', padding: '12px', background: loading ? '#aaa' : '#185FA5', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', marginTop: 4 }}>
              {loading ? '로그인 중...' : '로그인'}
            </button>
          </div>
        )}

        {/* ── 회원가입 폼 ── */}
        {tab === 'register' && (
          <div style={{ maxHeight: 460, overflowY: 'auto', paddingRight: 4 }}>

            <div style={divider}>기본 정보</div>

            <label style={lbl}>구분 <span style={required}>*</span></label>
            <select name="memberType" value={form.memberType} onChange={handleChange} style={inp}>
              {ACCOUNT_TYPE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            {form.memberType === 'display' ? (
              <div style={{ fontSize: 11, color: '#aaa', marginTop: -4, marginBottom: 10 }}>
                ※ 생산현황판 전용 계정은 직원이 아닌 생산현황 열람용 계정입니다. 직원현황에는 등록되지 않습니다.
              </div>
            ) : (
              <div style={{ fontSize: 11, color: '#aaa', marginTop: -4, marginBottom: 10 }}>
                ※ 부서·직급은 가입 후 관리자가 배정합니다.
              </div>
            )}

            <label style={lbl}>아이디 <span style={required}>*</span></label>
            <input name="username" placeholder="영문, 숫자 조합" value={form.username} onChange={handleChange} style={inp} />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={lbl}>비밀번호 <span style={required}>*</span></label>
                <input name="password" type="password" placeholder="6자 이상" value={form.password} onChange={handleChange} style={inp} />
              </div>
              <div>
                <label style={lbl}>비밀번호 확인 <span style={required}>*</span></label>
                <input name="confirmPassword" type="password" placeholder="다시 입력" value={form.confirmPassword} onChange={handleChange} style={inp} />
              </div>
            </div>

            {form.memberType === 'display' ? (
              <>
                <label style={lbl}>계정 이름 <span style={required}>*</span></label>
                <input name="name" placeholder="예: 1공장 현황판, 로비 모니터" value={form.name} onChange={handleChange} style={inp} />
                <div style={{ fontSize: 11, color: '#aaa', marginTop: -4, marginBottom: 4 }}>
                  ※ 이 계정은 직원현황에 등록되지 않으며, 승인 및 화면 권한은 관리자가 계정관리에서 부여합니다.
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={lbl}>이름 <span style={required}>*</span></label>
                    <input name="name" placeholder="실명 입력" value={form.name} onChange={handleChange} style={inp} />
                  </div>
                  <div>
                    <label style={lbl}>직급</label>
                    <select name="position" value={form.position} onChange={handleChange} style={inp}>
                      <option value="">선택 안 함</option>
                      {positionOptions.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>

                <div style={divider}>회사 정보</div>

                {form.memberType === 'partner' ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div>
                      <label style={lbl}>회사명</label>
                      <input name="company" placeholder="소속 회사명" value={form.company} onChange={handleChange} style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>담당부서</label>
                      <input name="department" placeholder="예: 영업팀, 품질팀" value={form.department} onChange={handleChange} style={inp} />
                    </div>
                  </div>
                ) : (
                  <>
                    <label style={lbl}>회사명</label>
                    <input name="company" placeholder="소속 회사명" value={form.company} onChange={handleChange} style={inp} />
                  </>
                )}

                <label style={lbl}>주소</label>
                <input name="address" placeholder="회사 또는 거주 주소" value={form.address} onChange={handleChange} style={inp} />

                <div style={divider}>연락처 정보</div>

                <label style={lbl}>연락처</label>
                <input name="phone" placeholder="010-0000-0000" value={form.phone} onChange={handleChange} style={inp} />

                <label style={lbl}>이메일</label>
                <input name="email" type="email" placeholder="example@email.com" value={form.email} onChange={handleChange} style={inp} />

                <label style={lbl}>긴급연락처</label>
                <input name="emergency_phone" placeholder="비상 시 연락 가능한 번호" value={form.emergency_phone} onChange={handleChange} style={inp} />
              </>
            )}

            <button onClick={handleRegister} disabled={loading}
              style={{ width: '100%', padding: '12px', background: loading ? '#aaa' : '#185FA5', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', marginTop: 8 }}>
              {loading ? '신청 중...' : '회원가입 신청'}
            </button>

            <div style={{ marginTop: 10, fontSize: 12, color: '#888', textAlign: 'center', lineHeight: 1.6 }}>
              ⚠️ 회원가입 후 관리자 승인이 완료되어야 로그인할 수 있습니다.
            </div>
          </div>
        )}

        {/* 메시지 */}
        {message && (
          <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8, fontSize: 13, textAlign: 'center', background: msgBg[msgType], color: msgColor[msgType] }}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
}